// StorageManager: high-level save/load for game progress.
// - Namespaces every key by gameId so multiple games can share a domain.
// - Stores a small envelope { v, t, data } for versioning + migration.
// - JSON in / JSON out; callers deal in plain objects.

import { MemoryAdapter } from './adapters/memoryAdapter.js';
import { LocalAdapter } from './adapters/localAdapter.js';
import { CloudAdapter } from './adapters/cloudAdapter.js';
import { SupabaseStorageAdapter } from './adapters/supabaseAdapter.js';
import { hasLocalStorage } from '../platform/env.js';

export class StorageManager {
  constructor(cfg, ctx, gameId, version) {
    this.log = ctx.log;
    this.gameId = gameId;
    this.version = version;
    this.prefix = `bf:${gameId}:`;
    this.backend = ctx.backend || null;
    this.adapter = this._pick(cfg);
    // When using a remote adapter we still mirror to local for offline safety.
    const remote = this.adapter instanceof CloudAdapter || this.adapter instanceof SupabaseStorageAdapter;
    this.mirror = remote && hasLocalStorage() ? new LocalAdapter() : null;
    this.migrate = ctx.migrate || null; // optional (oldData, oldVersion) => newData
  }

  _pick(cfg) {
    const choice = cfg.adapter === 'auto'
      ? (hasLocalStorage() ? 'local' : 'memory')
      : cfg.adapter;
    switch (choice) {
      case 'local': return hasLocalStorage() ? new LocalAdapter() : new MemoryAdapter();
      case 'cloud': return new CloudAdapter(cfg.cloud || {});
      case 'supabase': return new SupabaseStorageAdapter(this.backend, this.gameId);
      case 'memory':
      default: return new MemoryAdapter();
    }
  }

  _key(key) {
    return this.prefix + key;
  }

  async save(key, data) {
    const envelope = JSON.stringify({ v: this.version, t: Date.now(), data });
    try {
      await this.adapter.set(this._key(key), envelope);
      if (this.mirror) await this.mirror.set(this._key(key), envelope);
      this.log.debug('saved', key);
      return true;
    } catch (err) {
      this.log.warn('save failed; trying local mirror', err);
      if (this.mirror) {
        try { await this.mirror.set(this._key(key), envelope); return true; } catch {}
      }
      return false;
    }
  }

  async load(key, fallback = null) {
    let raw = null;
    try {
      raw = await this.adapter.get(this._key(key));
    } catch (err) {
      this.log.warn('load failed; trying local mirror', err);
      if (this.mirror) { try { raw = await this.mirror.get(this._key(key)); } catch {} }
    }
    if (!raw) return fallback;
    try {
      const env = JSON.parse(raw);
      if (env.v !== this.version && typeof this.migrate === 'function') {
        return this.migrate(env.data, env.v) ?? fallback;
      }
      return env.data ?? fallback;
    } catch {
      this.log.warn('corrupt save for', key);
      return fallback;
    }
  }

  async remove(key) {
    try { await this.adapter.remove(this._key(key)); } catch {}
    if (this.mirror) { try { await this.mirror.remove(this._key(key)); } catch {} }
  }
}
